export type UtmTouch = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_campaign_id?: string;
  utm_adset?: string;
  utm_adset_id?: string;
  utm_content?: string;
  utm_ad_id?: string;
  utm_placement?: string;
  utm_term?: string;
  utm_creative?: string;
  utm_platform?: string;
  fbclid?: string;
  landing_url?: string;
  referrer?: string;
  captured_at: string;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type CaptureOptions = {
  referrer?: string;
  now?: string;
};

export const FIRST_TOUCH_KEY = 'inspira_first_touch_utm';
export const LAST_TOUCH_KEY = 'inspira_last_touch_utm';

const TRACKING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_campaign_id',
  'utm_adset',
  'utm_adset_id',
  'utm_content',
  'utm_ad_id',
  'utm_placement',
  'utm_term',
  'utm_creative',
  'utm_platform',
  'fbclid',
] as const;

function browserStorage(): StorageLike | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function currentUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.href;
}

function currentReferrer(): string {
  if (typeof document === 'undefined') return '';
  return document.referrer || '';
}

function normalizeValue(value: string | null): string | undefined {
  const trimmed = String(value || '').trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, 500);
}

function parseStoredTouch(raw: string | null): UtmTouch | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || typeof parsed.captured_at !== 'string') {
      return null;
    }
    return parsed as UtmTouch;
  } catch {
    return null;
  }
}

export function getUtmParamsFromUrl(
  urlInput?: string | URL | null,
  options: CaptureOptions = {},
): UtmTouch | null {
  const resolvedUrl = urlInput?.toString() || currentUrl();
  if (!resolvedUrl) return null;

  let url: URL;
  try {
    url = new URL(resolvedUrl, typeof window !== 'undefined' ? window.location.origin : 'https://inspiramusicas.com.br');
  } catch {
    return null;
  }

  const touch: Partial<UtmTouch> = {};
  for (const key of TRACKING_PARAM_KEYS) {
    const value = normalizeValue(url.searchParams.get(key));
    if (value) touch[key] = value;
  }

  const hasTrackingParam = TRACKING_PARAM_KEYS.some((key) => Boolean(touch[key]));
  if (!hasTrackingParam) return null;

  touch.landing_url = url.href;
  touch.referrer = normalizeValue(options.referrer ?? currentReferrer()) || '';
  touch.captured_at = options.now || new Date().toISOString();

  return touch as UtmTouch;
}

export function persistUtmParams(
  touch: UtmTouch | null = getUtmParamsFromUrl(),
  storage: StorageLike | null = browserStorage(),
): UtmTouch | null {
  if (!touch || !storage) return null;

  const serialized = JSON.stringify(touch);
  if (!storage.getItem(FIRST_TOUCH_KEY)) {
    storage.setItem(FIRST_TOUCH_KEY, serialized);
  }
  storage.setItem(LAST_TOUCH_KEY, serialized);
  return touch;
}

export function persistUtmParamsFromCurrentUrl(): UtmTouch | null {
  return persistUtmParams(getUtmParamsFromUrl());
}

export function getFirstTouchUtm(storage: StorageLike | null = browserStorage()): UtmTouch | null {
  if (!storage) return null;
  return parseStoredTouch(storage.getItem(FIRST_TOUCH_KEY));
}

export function getLastTouchUtm(storage: StorageLike | null = browserStorage()): UtmTouch | null {
  if (!storage) return null;
  return parseStoredTouch(storage.getItem(LAST_TOUCH_KEY));
}

export function clearUtmParams(storage: StorageLike | null = browserStorage()): void {
  if (!storage) return;
  storage.removeItem(FIRST_TOUCH_KEY);
  storage.removeItem(LAST_TOUCH_KEY);
}

export function getStoredUtmPayload(storage: StorageLike | null = browserStorage()) {
  return {
    first_touch: getFirstTouchUtm(storage),
    last_touch: getLastTouchUtm(storage),
  };
}

